import { useMemo, useState } from 'react';
import { Plus, Search, ChevronUp, ChevronDown, Trash2, GripVertical, Inbox } from 'lucide-react';
import { useCreateService, useUpdateService, useDeleteService, useReorderServices } from './hooks.ts';
import { useAutosave } from './useAutosave.ts';
import { AutosaveStatus } from './AutosaveStatus.tsx';
import { SectionPanel } from './SectionPanel.tsx';
import { FormBuilder, type FormValues } from './FormBuilder.tsx';
import { SubmissionsPanel } from './SubmissionsPanel.tsx';
import type { GroupBundle, Service, ServiceInput, SiteBundle, SiteDocument } from './api.ts';

// One field's bilingual inputs (compact, for the service editor).
function Pair({ label, value, onChange, multiline }: { label: string; value: { dv: string; en: string }; onChange: (v: { dv: string; en: string }) => void; multiline?: boolean }): JSX.Element {
  const Dv = multiline ? 'textarea' : 'input';
  const En = multiline ? 'textarea' : 'input';
  return (
    <div className="space-y-1.5">
      <label className="status-label">{label}</label>
      <div className="grid gap-2 sm:grid-cols-2">
        <Dv dir="rtl" rows={multiline ? 3 : undefined} className={`${multiline ? 'textarea' : 'input'} thaana text-sm`} value={value.dv} onChange={(e) => onChange({ ...value, dv: (e.target as HTMLInputElement).value })} placeholder="ދިވެހި" />
        <En dir="ltr" rows={multiline ? 3 : undefined} className={`${multiline ? 'textarea' : 'input'} text-sm`} value={value.en} onChange={(e) => onChange({ ...value, en: (e.target as HTMLInputElement).value })} placeholder="English" />
      </div>
    </div>
  );
}

function ServiceForm({ service, newCount }: { service: Service; newCount: number }): JSX.Element {
  const update = useUpdateService();
  const [v, setV] = useState<Service>(service);
  const [tab, setTab] = useState<'details' | 'form' | 'inbox'>('details');
  const autosave = useAutosave((input: ServiceInput) => update.mutateAsync({ id: service.id, input }));

  // Autosave payload excludes `published` (owned by the row's toggle) and slug/id.
  const set = (patch: Partial<Service>): void =>
    setV((prev) => {
      const n = { ...prev, ...patch };
      autosave.schedule({
        nameDv: n.nameDv, nameEn: n.nameEn, summaryDv: n.summaryDv, summaryEn: n.summaryEn,
        feeDv: n.feeDv, feeEn: n.feeEn, sectionDv: n.sectionDv, sectionEn: n.sectionEn,
        howToApplyDv: n.howToApplyDv, howToApplyEn: n.howToApplyEn, requiredDocsDv: n.requiredDocsDv, requiredDocsEn: n.requiredDocsEn,
        formEnabled: n.formEnabled, formFields: n.formFields,
        formNoteDv: n.formNoteDv, formNoteEn: n.formNoteEn, formSuccessDv: n.formSuccessDv, formSuccessEn: n.formSuccessEn,
      });
      return n;
    });

  const formValues: FormValues = {
    formEnabled: v.formEnabled, formFields: v.formFields ?? [],
    formNoteDv: v.formNoteDv, formNoteEn: v.formNoteEn, formSuccessDv: v.formSuccessDv, formSuccessEn: v.formSuccessEn,
  };

  const tabCls = (t: string) => `rounded-md px-2.5 py-1 text-xs font-medium ${tab === t ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:text-foreground'}`;

  return (
    <div className="space-y-4 border-t border-border bg-muted/20 p-4">
      <div className="flex items-center gap-2">
        <div className="inline-flex rounded-lg border border-border p-0.5">
          <button type="button" className={tabCls('details')} onClick={() => setTab('details')}>Details</button>
          <button type="button" className={tabCls('form')} onClick={() => setTab('form')}>Application form</button>
          <button type="button" className={tabCls('inbox')} onClick={() => setTab('inbox')}>
            Submissions{newCount > 0 && <span className="ms-1 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[0.6rem] font-bold text-primary-foreground">{newCount}</span>}
          </button>
        </div>
        <span className="mono-meta ms-auto text-xs text-muted-foreground" dir="ltr">/services/{service.slug}</span>
        <AutosaveStatus status={autosave.status} />
      </div>

      {tab === 'details' && (
        <div className="space-y-4">
          <Pair label="Name" value={{ dv: v.nameDv, en: v.nameEn }} onChange={(x) => set({ nameDv: x.dv, nameEn: x.en })} />
          <Pair label="Short summary" value={{ dv: v.summaryDv, en: v.summaryEn }} onChange={(x) => set({ summaryDv: x.dv, summaryEn: x.en })} />
          <div className="grid gap-3 sm:grid-cols-2">
            <Pair label="Fee" value={{ dv: v.feeDv, en: v.feeEn }} onChange={(x) => set({ feeDv: x.dv, feeEn: x.en })} />
            <Pair label="Responsible section" value={{ dv: v.sectionDv, en: v.sectionEn }} onChange={(x) => set({ sectionDv: x.dv, sectionEn: x.en })} />
          </div>
          <Pair label="How to apply" value={{ dv: v.howToApplyDv, en: v.howToApplyEn }} onChange={(x) => set({ howToApplyDv: x.dv, howToApplyEn: x.en })} multiline />
          <Pair label="Required documents" value={{ dv: v.requiredDocsDv, en: v.requiredDocsEn }} onChange={(x) => set({ requiredDocsDv: x.dv, requiredDocsEn: x.en })} multiline />
        </div>
      )}

      {tab === 'form' && <FormBuilder values={formValues} onChange={(patch) => set(patch)} />}

      {tab === 'inbox' && (
        v.formEnabled
          ? <SubmissionsPanel serviceId={service.id} fields={v.formFields ?? []} />
          : <div className="flex flex-col items-center gap-2 p-6 text-center"><Inbox size={20} className="text-muted-foreground" /><p className="text-sm text-muted-foreground">Enable the application form to receive submissions.</p></div>
      )}
    </div>
  );
}

function ServiceRow({ service, index, total, expanded, onToggle, onMove, newCount }: { service: Service; index: number; total: number; expanded: boolean; onToggle: () => void; onMove: (dir: -1 | 1) => void; newCount: number }): JSX.Element {
  const update = useUpdateService();
  const del = useDeleteService();
  const move = onMove;

  return (
    <li className="card-paper overflow-hidden">
      <div className="flex items-center gap-2 p-3">
        <GripVertical size={15} className="shrink-0 text-muted-foreground/50" />
        <button type="button" onClick={onToggle} className="min-w-0 flex-1 text-start">
          <span className="block truncate font-medium text-foreground">{service.nameEn || service.nameDv || <span className="text-muted-foreground">Untitled service</span>}</span>
          {(service.nameDv && service.nameEn) && <span className="thaana block truncate text-xs text-muted-foreground" dir="rtl">{service.nameDv}</span>}
        </button>
        {newCount > 0 && <span className="pill pill-active shrink-0" title={`${newCount} new application(s)`}>{newCount} new</span>}
        {!service.published && <span className="pill pill-pending shrink-0">Hidden</span>}
        <button
          type="button" role="switch" aria-checked={service.published} aria-label="Published"
          disabled={update.isPending}
          onClick={() => update.mutate({ id: service.id, input: { published: !service.published } })}
          className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors ${service.published ? 'bg-primary' : 'bg-muted-foreground/30'}`}
        >
          <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${service.published ? 'translate-x-4.5' : 'translate-x-1'}`} style={{ transform: service.published ? 'translateX(1.05rem)' : 'translateX(0.15rem)' }} />
        </button>
        <div className="flex flex-col">
          <button type="button" aria-label="Move up" disabled={index === 0} onClick={() => move(-1)} className="text-muted-foreground hover:text-foreground disabled:opacity-20"><ChevronUp size={13} /></button>
          <button type="button" aria-label="Move down" disabled={index === total - 1} onClick={() => move(1)} className="text-muted-foreground hover:text-foreground disabled:opacity-20"><ChevronDown size={13} /></button>
        </div>
        <button type="button" aria-label="Delete service" onClick={() => { if (confirm('Delete this service?')) del.mutate(service.id); }} className="shrink-0 text-muted-foreground hover:text-destructive"><Trash2 size={15} /></button>
      </div>
      {expanded && <ServiceForm service={service} newCount={newCount} />}
    </li>
  );
}

// The Services page editor: the mandated intro field, then the managed list.
export function ServicesEditor({ bundle }: { bundle: SiteBundle }): JSX.Element {
  const servicesGroup = bundle.groups.find((g) => g.group === 'services') as GroupBundle;
  const create = useCreateService();
  const reorder = useReorderServices();
  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState<string | null>(null);

  const services = bundle.services;

  const move = (id: string, dir: -1 | 1): void => {
    const ids = services.map((s) => s.id);
    const i = ids.indexOf(id);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j]!, ids[i]!];
    reorder.mutate(ids);
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return services;
    return services.filter((s) => `${s.nameEn} ${s.nameDv} ${s.slug}`.toLowerCase().includes(q));
  }, [services, query]);

  const docs: SiteDocument[] = bundle.documents;

  return (
    <div className="space-y-8">
      {/* Intro (mandated field) */}
      <SectionPanel key="services-intro" group={servicesGroup} documents={docs} />

      {/* Service list */}
      <section className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h3 className="section-heading font-serif text-lg text-ink">Services ({services.length})</h3>
          <button
            type="button"
            className="btn-primary btn-sm inline-flex items-center gap-1.5"
            disabled={create.isPending}
            onClick={() => create.mutate({}, { onSuccess: (s) => setExpanded(s.id) })}
          >
            <Plus size={15} /> Add service
          </button>
        </div>

        {services.length > 6 && (
          <div className="relative">
            <Search size={15} className="pointer-events-none absolute inset-y-0 start-3 my-auto text-muted-foreground" />
            <input className="input ps-9 text-sm" placeholder="Search services…" value={query} onChange={(e) => setQuery(e.target.value)} />
          </div>
        )}

        {services.length === 0 ? (
          <div className="card-paper p-8 text-center">
            <p className="text-sm text-muted-foreground">No services yet. Add the first one residents can find and (soon) apply for.</p>
          </div>
        ) : (
          <ul className="space-y-2">
            {filtered.map((s) => (
              <ServiceRow
                key={s.id}
                service={s}
                index={services.indexOf(s)}
                total={services.length}
                expanded={expanded === s.id}
                onToggle={() => setExpanded((cur) => (cur === s.id ? null : s.id))}
                onMove={(dir) => move(s.id, dir)}
                newCount={bundle.submissionCounts[s.id] ?? 0}
              />
            ))}
            {filtered.length === 0 && <li className="p-4 text-center text-sm text-muted-foreground">No services match "{query}".</li>}
          </ul>
        )}
      </section>
    </div>
  );
}
